import h from 'hyperscript';

import arrowLeftIcon from '@disy/cadenza-icons/arrow-left.svg';
import moveIcon from '@disy/cadenza-icons/move.svg';
import trashIcon from '@disy/cadenza-icons/trash.svg';
import visibilityOffIcon from '@disy/cadenza-icons/visibility-off.svg';
import visibilityOnIcon from '@disy/cadenza-icons/visibility-on.svg';
import type { HyperScriptButtonOptions } from '@disy/ui/button';
import { createButton, createIconButton } from '@disy/ui/button';
import { icon } from '@disy/ui/icon';

import { createIconToggle } from 'ui/icon-toggle/icon-toggle';

import { getStore } from 'cadenza/store/cadenza-store';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { createChangeVisibilityCommandForLayersInPath } from 'cadenza/workbook/map/workbook-layer-utils';
import type { AttributeBundleDefinitionDto } from 'cadenza/workbook/model/attribute-bundle-definition-dto';
import { getViewTreeModel } from 'cadenza/workbook/state/workbook-layer-tree-model';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import { getAttributes } from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import type { AttributeBundleValidator } from 'cadenza/workbook/workbook-panel/settings/attributes/attribute-bundle-validator';
import type MapViewSettingsPanelContent from 'cadenza/workbook/workbook-panel/settings/map/map-view-settings-panel-content';
import i18n from 'cadenza/workbook/workbook-panel/settings/map/settings-util/settings-util.properties';
import type { ViewSettingsPanel } from 'cadenza/workbook/workbook-panel/settings/view-settings-panel/view-settings-panel';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';
import { resetActiveLayerId } from 'cadenza/workbook/workbook.state';

import './settings-util.css';

export function createGoBackToMapViewSettingsButton(mapViewSettingsPanelContent: MapViewSettingsPanelContent) {
  return createButton([icon(arrowLeftIcon), h('span.ellipsis', i18n('back.mapViewSettings'))], {
    size: 's',
    variant: 'link',
    styleClass: 'settings-util--back',
    onclick: () => {
      getStore().dispatch(resetActiveLayerId());
      // Prevents d-tabs refresh problems as well as offset-height calculation problems in the view settings
      mapViewSettingsPanelContent.closest<ViewSettingsPanel>(WorkbookPanelName.VIEW_SETTINGS)!.updatePanelContent(); // designer should be there
    },
  });
}

function createToolbarButton(
  svgMarkup: string,
  title: string,
  onclick: (event: MouseEvent) => void,
  options: HyperScriptButtonOptions = {},
) {
  return createIconButton(icon(svgMarkup), title, {
    variant: 'borderless',
    size: 's',
    onclick,
    ...options,
  });
}

export function createMoveButton(
  entityTitle: string,
  onclickFunction: (event: MouseEvent) => void,
  isDisabled: boolean,
) {
  return createToolbarButton(moveIcon, i18n('toolbar.action.move.title', { entityTitle }), onclickFunction, {
    disabled: isDisabled,
  });
}

export function createRemoveButton(entityTitle: string, onclickFunction: (event: MouseEvent) => void) {
  if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management')) {
    return createToolbarButton(trashIcon, i18n('toolbar.action.remove.title', { entityTitle }), onclickFunction);
  }
}

export function createToolbarIconVisibilityToggle(
  entityTitle: string,
  viewId: ViewId,
  layerId: LayerId,
  visible: boolean,
) {
  let iconToggle = createIconToggle(
    getVisibilityTitle(visible, entityTitle),
    icon(visibilityOnIcon),
    icon(visibilityOffIcon),
    {
      pressed: !visible,
      styleClass: 'settings-util--icon-toggle',
      size: 's',
      disabled: parentIsToggleLayer(visible, layerId, viewId),
      onclick: (event) => {
        visible = !visible;
        iconToggle.title = getVisibilityTitle(visible, entityTitle);
        iconToggle.disabled = parentIsToggleLayer(visible, layerId, viewId);
        toggleVisibility(viewId, layerId, visible);
        event.stopPropagation();
      },
    },
  );
  return iconToggle;
}

function parentIsToggleLayer(visible: boolean, layerId: LayerId, viewId: ViewId) {
  const treeModel = getViewTreeModel(getStore().getState(), viewId);
  const parentLayerId = treeModel.getLayerNode(layerId)?.parent?.id;
  const isNotRootLayerId = treeModel.rootNode.id !== parentLayerId;
  return (
    (visible && parentLayerId && isNotRootLayerId && treeModel.getLayerStructureType(parentLayerId) === 'TOGGLE') ||
    false
  );
}

function getVisibilityTitle(visible: boolean, entityTitle: string) {
  return i18n(`toolbar.action.${visible ? 'hide' : 'show'}.title`, { entityTitle });
}

export function toggleVisibility(viewId: ViewId, layerId: LayerId, visible: boolean) {
  const treeModel = getViewTreeModel(getStore().getState(), viewId);
  const layerNode = treeModel.getRequiredLayerNode(layerId);
  const command = createChangeVisibilityCommandForLayersInPath(viewId, treeModel, layerNode, visible);
  return getStore().dispatch(executeCommand(command));
}

export function attributeBundleAccepts(
  bundleDefinition: AttributeBundleDefinitionDto,
  validator: AttributeBundleValidator,
  attribute: WorkbookAttributeDto,
) {
  // CADENZA-42501: This is not adding the hierarchy to the picker (yet), it's just about deciding whether the hierarchy
  // *and/or its levels* should be available for selection later.
  // While the bundle validator might not accept the hierarchy itself the levels might be just fine and so the hierarchy
  // itself has to be recognized as supported too. When rendering the options later the hierarchy itself will be shown as
  // deactivated while the levels will be selectable.
  if (attribute.hierarchyType === 'MULTI_ATTRIBUTE_HIERARCHY') {
    const levelAttributes = getAttributes(getStore().getState()).filter((att) => att.hierarchyId === attribute.id);
    return levelAttributes.some(
      (att) => validator.dataViewControllerAccepts(bundleDefinition, att, true, false).accepts,
    );
  } else {
    return validator.dataViewControllerAccepts(bundleDefinition, attribute, true, false).accepts;
  }
}
